import { useWindowSize } from '@uidotdev/usehooks';
import { Spinner, Tabs } from 'flowbite-react';
import type { FC } from 'react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardSearchContext } from '../..';
import ChartTable from '../../../../components/table';
import type {
  IChartCard,
  IChartCardDefaultItem,
  IChartLabels,
  ILineConfigItem,
  IResponseTypeLineItem,
  IScatterConfigItem,
  ITableData,
  ITreeMapConfigItem,
} from '../../types';
import { ChartsTypeEnum } from '../../types';
import { BarStackChart, LineChart, ScatterChart, TreeMap } from './charts';
import { DownloadTableIcon, TableIcon } from './icons';
import { activeTabConfigMemo, activeTableDataMemo } from './memo';

import HoverTooltip from '@/components/hover-tooltip';
import { read, writeFile } from 'xlsx';
import { GraphAxiesEnum } from '../../types/enums';

type IChartWrapperProps = {
  card: IChartCard | IChartCardDefaultItem;
  isLoading: boolean;
  isNoFilters: boolean;
  activeBrands: string[];
};

const chartsMap = {
  [ChartsTypeEnum.scatter]: ScatterChart,
  [ChartsTypeEnum.line]: LineChart,
  [ChartsTypeEnum.treeMap]: TreeMap,
  [ChartsTypeEnum.barStack]: BarStackChart,
};

type IChartComponentConfig = IScatterConfigItem[] &
  ILineConfigItem[] &
  ITreeMapConfigItem[];

const ChartWrapper: FC<IChartWrapperProps> = ({
  card,
  isLoading,
  isNoFilters,
  activeBrands,
}) => {
  const { width } = useWindowSize();
  const [showChart, setShowChart] = useState(false);
  const { title, subtitle, type } = card;
  const { data } = card as IResponseTypeLineItem;
  const { tabs } = card as IChartCard;

  const height = (width as number) > 650 ? 500 : 350;

  const [activeTabCard, setActiveTabCard] = useState(0);
  const [activeTabDate, setActiveTabDate] = useState(0);
  const [isShowTable, setIsShowTable] = useState(false);
  const chartRef = useRef<HTMLDivElement | null>(null);

  const labels: IChartLabels | null = useMemo(() => {
    if (type === ChartsTypeEnum.scatter)
      return { x: GraphAxiesEnum.sop, y: GraphAxiesEnum.averagePositon };
    if (type === ChartsTypeEnum.line) return { x: GraphAxiesEnum.date, y: '' };
    return null;
  }, [type]);

  const { onlyMyBrandsMap } = useContext(DashboardSearchContext);

  const activeTabConfig:
    | IScatterConfigItem[]
    | ILineConfigItem[]
    | ITreeMapConfigItem[] = useMemo(() => {
    const config = activeTabConfigMemo(
      tabs,
      data,
      activeTabCard,
      activeTabDate,
      type as ChartsTypeEnum,
      onlyMyBrandsMap,
      activeBrands
    );
    if (!config.length) setIsShowTable(false);
    return config;
  }, [
    tabs,
    data,
    activeTabCard,
    activeTabDate,
    type,
    activeBrands,
    onlyMyBrandsMap,
  ]);

  const { tableHeaders, tableData }: ITableData = useMemo(
    () =>
      activeTableDataMemo(
        activeTabConfig,
        type as ChartsTypeEnum,
        labels,
        tabs?.[activeTabCard]?.title as string
      ),
    [activeTabConfig, type, labels, activeTabCard, tabs]
  );

  const onActiveTabChangeDate = (index: number) => setActiveTabDate(index);
  const onActiveTabChangeCards = (index: number) => setActiveTabCard(index);

  const downloadTableDataInXlsx = () => {
    let excelData = '';
    tableHeaders.forEach((rowItem) => {
      excelData += rowItem?.replaceAll(',', '') + ',';
    });
    excelData += '\r\n';
    tableData.forEach((rowItem) => {
      rowItem.forEach((colItem) => {
        excelData += String(colItem)?.replaceAll(',', '') + ',';
      });
      excelData += '\r\n';
    });
    // excelData = 'data:text/xlsx,' + encodeURI(excelData);
    // const a = document.createElement('A');
    // a.setAttribute('href', excelData);
    // a.setAttribute('download', `table_${Date.now()}.xlsx`);
    // document.body.appendChild(a);
    // a.click();

    const wb = read(excelData, { type: 'string' });
    writeFile(wb, `table_${Date.now()}.xlsx`);
  };

  useEffect(() => {
    const element = chartRef.current;
    let timeout: string | number | NodeJS.Timeout | undefined;
    if (element !== null) {
      const observer = new ResizeObserver((entries) => {
        clearTimeout(timeout);
        setShowChart(false);
        if (typeof entries[0]?.target?.clientWidth === 'number') {
          timeout = setTimeout(() => {
            setShowChart(true);
          }, 100);
        }
      });
      observer.observe(element);
      return () => observer.unobserve(element);
    }
    return;
  }, [chartRef]);

  const ChartComponent = chartsMap[type as keyof typeof chartsMap];

  const iconButtons = [
    {
      onClick: () => setIsShowTable((prev) => !prev),
      className: `${
        activeTabConfig?.length
          ? ''
          : 'pointer-events-none opacity-50 transition-opacity'
      }`,
      icon: <TableIcon />,
    },
    {
      onClick: downloadTableDataInXlsx,
      className: `ml-3 ${
        activeTabConfig?.length
          ? ''
          : 'pointer-events-none opacity-50 transition-opacity'
      }`,
      icon: <DownloadTableIcon />,
    },
  ];

  return (
    <div
      ref={chartRef}
      className={`${'min-w-full'} white-block max-w-full grow basis-0 rounded-lg bg-white p-5 shadow`}
    >
      <div className="flex items-center justify-between border-b pb-2">
        <div>
          <div className="pb-2 text-xl font-bold leading-9">{title}</div>
          <div className="text-sm font-normal text-gray-500">{subtitle}</div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {data?.length > 0 && (
            <Tabs.Group
              className="items-center justify-end gap-0"
              theme={{
                tabitemcontainer: { base: 'h-0' },
                tablist: {
                  tabitem: {
                    base: 'p-2 px-3 !rounded-full ml-2',
                  },
                },
              }}
              color="primary"
              style="pills"
              onActiveTabChange={onActiveTabChangeDate}
            >
              {data.map(({ title }) => (
                <Tabs.Item active title={title} key={title} />
              ))}
            </Tabs.Group>
          )}
          <div className="ml-3 flex items-center">
            {iconButtons.map(({ onClick, className, icon }, index) => (
              <button key={index} onClick={onClick} className={className}>
                {icon}
              </button>
            ))}
          </div>
        </div>
      </div>

      {tabs?.length > 0 && (
        <Tabs.Group
          className="mt-2 w-full border-0 text-primary-600"
          style="underline"
          theme={{
            tablist: {
              tabitem: { styles: { underline: { base: '!z-10' } } },
            },
          }}
          onActiveTabChange={onActiveTabChangeCards}
        >
          {tabs.map(({ title, tooltip, description, labels }, index) => (
            <Tabs.Item
              key={title}
              title={
                <div className="flex items-center justify-center gap-1 text-[.775rem] leading-5">
                  {title}{' '}
                  {tooltip && (
                    <HoverTooltip
                      title={title}
                      text={tooltip}
                      placement="right"
                      className="max-w-[50%]"
                    />
                  )}
                </div>
              }
            >
              {index === activeTabCard && (
                <div
                  className={`${
                    height === 500 ? 'min-h-[500px]' : 'min-h-[300px]'
                  } relative flex h-full flex-col items-center justify-center`}
                >
                  {isLoading && (
                    <div
                      className={`absolute z-10 flex h-full w-full items-center justify-center`}
                    >
                      <Spinner size="xl" />
                    </div>
                  )}
                  {!isLoading &&
                    !isNoFilters &&
                    activeTabConfig?.length !== 0 && (
                      <>
                        <ChartComponent
                          labels={labels as IChartLabels}
                          data={activeTabConfig as IChartComponentConfig}
                          height={height}
                          showChart={showChart}
                          yAxisInverse={
                            type === ChartsTypeEnum.line &&
                            tabs[activeTabCard]?.key ===
                              GraphAxiesEnum.averagePositon
                          }
                        />
                        {isShowTable && (
                          <ChartTable
                            tableHeaders={tableHeaders}
                            tableData={tableData}
                          />
                        )}

                        <div className="mt-2 min-h-[40px] text-xs italic text-black">
                          {description}
                        </div>
                      </>
                    )}
                  {!isLoading &&
                    !isNoFilters &&
                    activeTabConfig.length === 0 && (
                      <div className="flex h-full w-full items-center justify-center text-black dark:text-white">
                        Нет данных по этим фильтрам
                      </div>
                    )}
                  {!isLoading && isNoFilters && (
                    <div className="flex h-full w-full items-center justify-center text-black dark:text-white">
                      Выберите все необходимые фильтры для загрузки графиков
                    </div>
                  )}
                </div>
              )}
            </Tabs.Item>
          ))}
        </Tabs.Group>
      )}
    </div>
  );
};

export default ChartWrapper;
